import { useCallback, useEffect, useMemo, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Button, Dropdown, Form, InputGroup, Modal, Pagination } from "react-bootstrap";
import { BsFilter, BsSearch } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFindUpcomingEvents } from "../../api/queries/useFindUpcomingEvents";
import { useGetBookedEvents } from "../../api/queries/useGetBookedEvents";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { useAppContext } from "../../Context/AppContext";
import { BookedEvents, Event } from "../../types/Types";
import {
  isEventDateStillActive,
  isEventPastForAttendedList,
  parseEventDateAsLocalDay,
} from "../../utils/eventDateUtils";
import { formatCurrencyAmount } from "../../utils/formatCurrency";
import {
  getEventObjectiveDisplayLabel,
  getEventStageDisplayLabel,
} from "../../i18n/eventRegistrationI18n";
import { ensureExplorerProfileSyncedForEventBooking } from "../../explorer/explorerProfileSync";
import { useExplorerConnectionLimitFeedback } from "../../hooks/useExplorerConnectionLimitFeedback";
import { EventListCard } from "./EventListCard";
import { FilterDropdown, FilterMenuItem, SearchableFilterDropdown } from "./EventFilterDropdowns";
import {
  EMPTY_FILTERS,
  EN_MONTH_LONG,
  EN_MONTH_SHORT,
  EVENT_ATTENDEES_PATH,
  EVENT_QUESTIONS_PATH,
  PAGE_SIZE,
  TABS,
  eventMatchesListFilters,
  readSavedEventsFromStorage,
  sortByDate,
  writeSavedEventsToStorage,
  type EventListFilters,
  type EventsListMode,
} from "./eventsListShared";

function Events() {
  const { t, i18n } = useTranslation();
  const langKey = i18n.resolvedLanguage || i18n.language;
  const navigate = useNavigate();
  const { objectId } = useAppContext();
  const { limitModal } = useExplorerConnectionLimitFeedback({
    autoShowPaywall: true,
  });

  const [eventSearch, setEventSearch] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [cityMenuSearch, setCityMenuSearch] = useState("");
  const [countryMenuSearch, setCountryMenuSearch] = useState("");
  const [appliedFilters, setAppliedFilters] =
    useState<EventListFilters>(EMPTY_FILTERS);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<BookedEvents | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<EventsListMode>("upcoming");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [savedEvents, setSavedEvents] = useState<Event[]>(
    readSavedEventsFromStorage,
  );

  useEffect(() => {
    void ensureExplorerProfileSyncedForEventBooking();
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearchTerm(eventSearch), 500);
    return () => clearTimeout(id);
  }, [eventSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, activeTab, appliedFilters]);

  const { data: events, isPending, error } = useFindUpcomingEvents({
    pageSize: PAGE_SIZE,
    page: currentPage,
    searchTerm: debouncedSearchTerm.trim() || undefined,
    enabled: activeTab === "upcoming",
  });
  const {
    data: bookedEvents,
    isPending: bookedEventsPending,
    error: bookedEventsError,
  } = useGetBookedEvents({ id: objectId || "", enabled: !!objectId });

  const patchFilters = useCallback(
    (patch: Partial<EventListFilters>) =>
      setAppliedFilters((prev) => ({ ...prev, ...patch })),
    [],
  );

  const handleBook = useCallback(
    async (eventId: number) => {
      try {
        await ensureExplorerProfileSyncedForEventBooking();
      } catch {
        /* best-effort */
      }
      navigate(`${EVENT_QUESTIONS_PATH}/${eventId}`);
    },
    [navigate],
  );

  const handleViewBooked = useCallback((event: BookedEvents) => {
    setSelectedEvent(event);
    setShowModal(true);
  }, []);

  const handleViewAttendees = useCallback(
    (eventId: number) => navigate(`${EVENT_ATTENDEES_PATH}/${eventId}`),
    [navigate],
  );

  const handleToggleFavorite = useCallback((event: Event) => {
    setSavedEvents((prev) => {
      const next = prev.some((e) => e.id === event.id)
        ? prev.filter((e) => e.id !== event.id)
        : [...prev, event];
      writeSavedEventsToStorage(next);
      return next;
    });
  }, []);

  const apiEventsActive = useMemo(
    () =>
      (events?.data ?? []).filter((e) => isEventDateStillActive(e.eventDate)),
    [events?.data],
  );

  const searchQ = useMemo(
    () => debouncedSearchTerm.trim().toLowerCase(),
    [debouncedSearchTerm],
  );

  const matchesSearch = useCallback(
    (event: Event) => {
      if (!searchQ) return true;
      const haystack =
        `${event.name ?? ""} ${event.description ?? ""} ${event.location ?? ""}`.toLowerCase();
      if (haystack.includes(searchQ)) return true;
      const day = parseEventDateAsLocalDay(event.eventDate);
      if (!day) return false;
      const m = day.getMonth();
      const n = m + 1;
      return (
        searchQ === String(n) ||
        searchQ === String(n).padStart(2, "0") ||
        searchQ === EN_MONTH_LONG[m] ||
        searchQ === EN_MONTH_SHORT[m]
      );
    },
    [searchQ],
  );

  const { bookedAsEvents, bookedLookup } = useMemo(() => {
    const list: BookedEvents[] = Array.isArray(bookedEvents)
      ? bookedEvents
      : bookedEvents
        ? [bookedEvents as BookedEvents]
        : [];
    const map = new Map<number, BookedEvents>();
    for (const b of list) if (!map.has(b.id)) map.set(b.id, b);
    const asEvents: Event[] = Array.from(map.values()).map((b) => ({
      id: b.id,
      name: b.name,
      description: b.description ?? "",
      price: b.price,
      earlyBirdPrice: b.earlyBirdPrice,
      earlyBirdDateEnd: b.earlyBirdDateEnd ?? "",
      seats: b.seats ?? 0,
      location: b.location ?? "",
      productId: "",
      currency: b.currency ?? undefined,
      eventDate: b.eventDate,
    }));
    return {
      bookedAsEvents: asEvents,
      bookedLookup: Array.isArray(bookedEvents) ? map : null,
    };
  }, [bookedEvents]);

  const savedIds = useMemo(
    () => new Set(savedEvents.map((e) => e.id)),
    [savedEvents],
  );

  const getBookedEvent = useCallback(
    (id: number) => bookedLookup?.get(id),
    [bookedLookup],
  );

  const eventsForOptions = useMemo(() => {
    if (activeTab === "upcoming") return apiEventsActive;
    if (activeTab === "saved") return savedEvents;
    return bookedAsEvents.filter((e) =>
      isEventPastForAttendedList(e.eventDate),
    );
  }, [activeTab, apiEventsActive, bookedAsEvents, savedEvents]);

  const { availableCities, availableCountries } = useMemo(() => {
    const cities = new Set<string>();
    const countries = new Set<string>();
    for (const event of eventsForOptions) {
      const loc = event.location?.trim();
      if (!loc) continue;
      const first = loc.indexOf(",");
      const last = loc.lastIndexOf(",");
      cities.add(first >= 0 ? loc.slice(0, first).trim() || loc : loc);
      if (last >= 0) {
        const c = loc.slice(last + 1).trim();
        if (c) countries.add(c);
      }
    }
    const byName = (a: string, b: string) => a.localeCompare(b);
    return {
      availableCities: Array.from(cities).sort(byName),
      availableCountries: Array.from(countries).sort(byName),
    };
  }, [eventsForOptions]);

  const eventList = useMemo(() => {
    const apply = (list: Event[], desc = false) =>
      sortByDate(
        list
          .filter((e) => !searchQ || matchesSearch(e))
          .filter((e) => eventMatchesListFilters(e, appliedFilters)),
        desc,
      );

    if (activeTab === "upcoming") {
      return sortByDate(
        apiEventsActive.filter((e) =>
          eventMatchesListFilters(e, appliedFilters),
        ),
      );
    }
    if (activeTab === "attended") {
      return apply(
        bookedAsEvents.filter((e) =>
          isEventPastForAttendedList(e.eventDate),
        ),
        true,
      );
    }
    return apply([...savedEvents]);
  }, [
    activeTab,
    apiEventsActive,
    appliedFilters,
    bookedAsEvents,
    matchesSearch,
    savedEvents,
    searchQ,
  ]);

  const totalPages = Math.ceil((events?.totalRecords ?? 0) / PAGE_SIZE);
  const showPagination =
    activeTab === "upcoming" && (events?.totalRecords ?? 0) >= PAGE_SIZE;
  const isListLoading =
    activeTab === "upcoming"
      ? isPending
      : activeTab === "attended"
        ? bookedEventsPending
        : false;
  const listError =
    activeTab === "upcoming"
      ? error
      : activeTab === "attended"
        ? bookedEventsError
        : null;

  const selectedDynamicFields = useMemo(
    () =>
      selectedEvent
        ? {
            name: selectedEvent.name ?? "",
            description: selectedEvent.description ?? "",
            location: selectedEvent.location ?? "",
          }
        : {},
    [selectedEvent],
  );
  const { data: translatedSelected } = useTranslateDynamicFields(
    selectedDynamicFields,
    langKey,
  );
  const txSelected = (
    key: keyof typeof selectedDynamicFields,
    fallback: string,
  ) => translatedSelected?.[key]?.trim() || fallback;

  const closeModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const priceLabel =
    appliedFilters.price === "free"
      ? t("events.filterPriceFree")
      : appliedFilters.price === "paid"
        ? t("events.filterPricePaid")
        : t("events.filterPrice");

  const locationFilters = [
    {
      id: "events-city-filter-toggle",
      value: appliedFilters.city,
      options: availableCities,
      menuSearch: cityMenuSearch,
      onMenuSearch: setCityMenuSearch,
      field: "city" as const,
      icon: "bi-geo-alt",
      label: t("events.filterCity"),
      placeholder: t("events.filterCityPlaceholder"),
      clearLabel: t("events.clearCityFilter"),
      emptyLabel: t("events.noCitiesAvailable"),
    },
    {
      id: "events-country-filter-toggle",
      value: appliedFilters.country,
      options: availableCountries,
      menuSearch: countryMenuSearch,
      onMenuSearch: setCountryMenuSearch,
      field: "country" as const,
      icon: "bi-globe2",
      label: t("events.filterCountry"),
      placeholder: t("events.filterCountryPlaceholder"),
      clearLabel: t("events.clearCountryFilter"),
      emptyLabel: t("events.noCountriesAvailable"),
    },
  ];

  const bookedDetailRows = selectedEvent
    ? [
        {
          label: t("events.price"),
          value: formatCurrencyAmount(
            selectedEvent.price,
            selectedEvent.currency ?? "gbp",
          ),
        },
        {
          label: t("events.eventDate"),
          value:
            parseEventDateAsLocalDay(
              selectedEvent.eventDate,
            )?.toLocaleDateString(langKey, {
              day: "numeric",
              month: "short",
              year: "numeric",
            }) || t("common.na"),
        },
        {
          label: t("events.sectorsOfInterest"),
          value: selectedEvent.sectorsOfInterest,
        },
        {
          label: t("events.stageOfBusiness"),
          value: getEventStageDisplayLabel(selectedEvent.stageOfBusiness, t),
        },
        {
          label: t("events.eventObjectives"),
          value: getEventObjectiveDisplayLabel(selectedEvent.eventObjectives, t),
        },
        {
          label: t("events.location"),
          value:
            txSelected("location", selectedEvent.location ?? "") ||
            t("common.na"),
        },
      ]
    : [];

  return (
    <main
      key={langKey}
      className="page-content w-100 pb-4"
      aria-label={t("events.listingAria")}
    >
      <ul className="nav nav-tabs mb-4" role="tablist">
        {TABS.map((tab) => (
          <li key={tab.id} className="nav-item" role="presentation">
            <button
              type="button"
              className={`nav-link d-inline-flex align-items-center gap-2 ${
                activeTab === tab.id ? "active text-primary" : "text-dark"
              }`}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`bi ${tab.icon}`} aria-hidden />
              <span
                className={
                  activeTab === tab.id ? "d-inline" : "d-none d-md-inline"
                }
              >
                {t(tab.labelKey)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <div className="d-flex d-md-none align-items-center gap-2 mb-3">
        <InputGroup className="rounded-3 border bg-white overflow-hidden shadow-sm flex-grow-1">
          <InputGroup.Text className="bg-white border-0 text-dark pe-0">
            <BsSearch
              size={14}
              style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,.35))" }}
              aria-hidden
            />
          </InputGroup.Text>
          <Form.Control
            type="text"
            className="border-0 shadow-none"
            placeholder={t("events.searchByEvent")}
            value={eventSearch}
            onChange={(e) => setEventSearch(e.target.value)}
            aria-label={t("events.searchByEvent")}
          />
        </InputGroup>
        <div className="d-flex gap-2 ms-auto flex-shrink-0">
          <Button
            type="button"
            variant="outline-primary"
            className="rounded-2 shadow-none d-flex align-items-center justify-content-center"
            aria-label={t("events.filters")}
            aria-expanded={showMobileFilters}
            onClick={() => {
              setShowMobileFilters((prev) => !prev);
            }}
          >
            <BsFilter size={18} aria-hidden />
          </Button>
        </div>
      </div>

      <div className="row g-3 mb-4 align-items-center">
        <div className="d-none d-md-block col-12 col-lg-4">
          <InputGroup className="rounded-3 border bg-white overflow-hidden shadow-sm w-100">
            <InputGroup.Text className="bg-white border-0 text-dark pe-0">
              <BsSearch
                size={14}
                style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,.35))" }}
                aria-hidden
              />
            </InputGroup.Text>
            <Form.Control
              type="text"
              className="border-0 shadow-none"
              placeholder={t("events.searchByEvent")}
              value={eventSearch}
              onChange={(e) => setEventSearch(e.target.value)}
              aria-label={t("events.searchByEvent")}
            />
          </InputGroup>
        </div>

        {locationFilters.map((f) => (
          <div
            key={f.id}
            className={`col-6 col-md-3 col-lg ${
              showMobileFilters ? "d-block" : "d-none"
            } d-md-block`}
          >
            <SearchableFilterDropdown
              id={f.id}
              value={f.value}
              options={f.options}
              menuSearch={f.menuSearch}
              onMenuSearch={f.onMenuSearch}
              onSelect={(v) => {
                patchFilters({ [f.field]: v });
                f.onMenuSearch("");
              }}
              onClear={() => {
                patchFilters({ [f.field]: "" });
                f.onMenuSearch("");
              }}
              icon={f.icon}
              label={f.label}
              placeholder={f.placeholder}
              clearLabel={f.clearLabel}
              emptyLabel={f.emptyLabel}
              style={{ width: "100%", minWidth: 0 }}
            />
          </div>
        ))}

        <div
          className={`col-6 col-md-3 col-lg ${
            showMobileFilters ? "d-block" : "d-none"
          } d-md-block`}
        >
          <FilterDropdown
            id="events-price-filter-toggle"
            active={Boolean(appliedFilters.price)}
            label={priceLabel}
            icon="bi-tag"
            style={{ width: "100%", minWidth: 0 }}
          >
            {(
              [
                ["", "events.filterPriceAny"],
                ["free", "events.filterPriceFree"],
                ["paid", "events.filterPricePaid"],
              ] as const
            ).map(([price, key]) => (
              <FilterMenuItem
                key={key}
                selected={appliedFilters.price === price}
                className="rounded-3"
                onClick={() => patchFilters({ price })}
              >
                {t(key)}
              </FilterMenuItem>
            ))}
          </FilterDropdown>
        </div>

        <div
          className={`col-6 col-md-3 col-lg ${
            showMobileFilters ? "d-block" : "d-none"
          } d-md-block`}
        >
          <FilterDropdown
            id="events-early-bird-filter-toggle"
            active={appliedFilters.earlyBirdOnly}
            label={t("events.filterEarlyBirdShort")}
            ariaLabel={t("events.filterEarlyBird")}
            icon="bi-clock"
            style={{ width: "100%", minWidth: 0 }}
          >
            <FilterMenuItem
              selected={!appliedFilters.earlyBirdOnly}
              className="rounded-3 small"
              onClick={() => patchFilters({ earlyBirdOnly: false })}
            >
              {t("events.filterEarlyBirdAny")}
            </FilterMenuItem>
            <FilterMenuItem
              selected={appliedFilters.earlyBirdOnly}
              className="rounded-3 small text-wrap"
              onClick={() => patchFilters({ earlyBirdOnly: true })}
            >
              {t("events.filterEarlyBird")}
            </FilterMenuItem>
          </FilterDropdown>
        </div>
      </div>

      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 align-items-stretch">
        {isListLoading ? (
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t("messages.loading")}</span>
            </div>
          </div>
        ) : listError ? (
          <div className="col-12">
            <p className="text-danger">{t("events.loadFailed")}</p>
          </div>
        ) : eventList.length > 0 ? (
          eventList.map((event) => (
            <div className="col d-flex" key={event.id}>
              <EventListCard
                event={event}
                listMode={activeTab}
                isBooked={
                  activeTab === "attended"
                    ? true
                    : !!bookedLookup?.has(event.id)
                }
                isFavorite={savedIds.has(event.id)}
                onToggleFavorite={handleToggleFavorite}
                onBook={handleBook}
                onViewBooked={handleViewBooked}
                onViewAttendees={handleViewAttendees}
                getBookedEvent={getBookedEvent}
              />
            </div>
          ))
        ) : (
          <div className="col-12">
            {activeTab === "attended" ? (
              <p className="text-muted mb-0">{t("events.noAttendedEvents")}</p>
            ) : activeTab === "saved" ? (
              <p className="text-muted mb-0">{t("events.noSavedEvents")}</p>
            ) : (
              <div className="text-muted">
                <p className="mb-2">{t("events.noMatch")}</p>
                <p className="mb-1">{t("events.noMatchTry")}</p>
                <ul className="mb-0 ps-3">
                  <li>{t("events.noMatchClearFilters")}</li>
                  <li>{t("events.noMatchNearbyCities")}</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {!!events?.totalRecords && showPagination && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Pagination.Item
                key={page}
                active={page === currentPage}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("events.bookedDetails")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <>
              <h5>{txSelected("name", selectedEvent.name)}</h5>
              <p>
                {txSelected("description", selectedEvent.description ?? "")}
              </p>
              <ul className="list-unstyled small">
                {bookedDetailRows.map((row) => (
                  <li key={row.label}>
                    <strong>{row.label}:</strong> {row.value}
                  </li>
                ))}
              </ul>
              <div className="d-flex justify-content-center mt-3">
                <Button
                  variant="danger"
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setShowCancelModal(true);
                  }}
                >
                  {t("events.cancelBooking")}
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            {t("common.close")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("events.cancelBooking")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {t("events.cancelBookingHelp")} <strong>enquiries@smbf.global</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            type="button"
            onClick={() => setShowCancelModal(false)}
          >
            {t("common.close")}
          </Button>
        </Modal.Footer>
      </Modal>
      {limitModal}
    </main>
  );
}

export default Events;
